import { createSlice } from "@reduxjs/toolkit";
import { loadFiltersFromStorage, saveFiltersToStorage } from "./filterPersistenceStorage";

function sanitizeModuleFilters(filters) {
  const next = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") {
      return;
    }
    next[key] = value;
  });

  return next;
}

function persistState(state) {
  saveFiltersToStorage(state);
}

const initialState = loadFiltersFromStorage();

const filterPersistenceSlice = createSlice({
  name: "filters",
  initialState,
  reducers: {
    setModuleFilters: (state, action) => {
      const { moduleKey, filters } = action.payload;
      const sanitized = sanitizeModuleFilters(filters);

      if (Object.keys(sanitized).length === 0) {
        delete state[moduleKey];
      } else {
        state[moduleKey] = sanitized;
      }

      persistState(state);
    },
    updateFilter: (state, action) => {
      const { moduleKey, key, value } = action.payload;
      const current = state[moduleKey] ?? {};

      if (value === null || value === undefined || value === "") {
        const next = { ...current };
        delete next[key];

        if (Object.keys(next).length === 0) {
          delete state[moduleKey];
        } else {
          state[moduleKey] = next;
        }
      } else {
        state[moduleKey] = {
          ...current,
          [key]: value,
        };
      }

      persistState(state);
    },
    removeFilter: (state, action) => {
      const { moduleKey, key } = action.payload;
      const current = state[moduleKey];
      if (!current || !(key in current)) {
        return;
      }

      const next = { ...current };
      delete next[key];

      if (Object.keys(next).length === 0) {
        delete state[moduleKey];
      } else {
        state[moduleKey] = next;
      }

      persistState(state);
    },
    clearModuleFilters: (state, action) => {
      const { moduleKey } = action.payload;
      delete state[moduleKey];
      persistState(state);
    },
    clearAllFilters: (state) => {
      Object.keys(state).forEach((moduleKey) => {
        delete state[moduleKey];
      });
      persistState(state);
    },
    hydrateFilters: (state, action) => {
      const hydrated = loadFiltersFromStorage();
      const incoming = action.payload ?? hydrated;

      Object.keys(state).forEach((moduleKey) => {
        delete state[moduleKey];
      });

      Object.entries(incoming).forEach(([moduleKey, filters]) => {
        const sanitized = sanitizeModuleFilters(filters);
        if (Object.keys(sanitized).length > 0) {
          state[moduleKey] = sanitized;
        }
      });

      persistState(state);
    },
  },
});

export const {
  setModuleFilters,
  updateFilter,
  removeFilter,
  clearModuleFilters,
  clearAllFilters,
  hydrateFilters,
} = filterPersistenceSlice.actions;

export default filterPersistenceSlice.reducer;
